import { useMemo } from 'react';
import { Control, Controller, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Label,
  Input,
  Switch,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@artstorefronts/ui';
import { GET_CATEGORIES } from '../graphql/queries';

export type ArtworkMediumType = 'photography' | 'traditional' | 'digital' | 'mixed';

export interface ArtworkSpecsFormValues {
  mediumType?: ArtworkMediumType | '';
  medium?: string;
  technique?: string;
  subMedium?: string;
  software?: string;
  camera?: string;
  lens?: string;
  focalLength?: string;
  aperture?: string;
  shutterSpeed?: string;
  iso?: string;
  mediumCategoryId?: string;
  hiddenFields: string[];
}

type SpecFieldKey = Exclude<keyof ArtworkSpecsFormValues, 'hiddenFields' | 'mediumType' | 'mediumCategoryId'>;

const FIELDS_BY_MEDIUM: Record<ArtworkMediumType, SpecFieldKey[]> = {
  photography: ['camera', 'lens', 'focalLength', 'aperture', 'shutterSpeed', 'iso', 'medium', 'technique'],
  traditional: ['medium', 'technique', 'subMedium'],
  digital: ['software', 'medium', 'technique'],
  mixed: [
    'medium',
    'technique',
    'subMedium',
    'software',
    'camera',
    'lens',
    'focalLength',
    'aperture',
    'shutterSpeed',
    'iso',
  ],
};

const FIELD_LABEL_KEYS: Record<SpecFieldKey, string> = {
  medium: 'medium',
  technique: 'technique',
  subMedium: 'sub-medium',
  software: 'software',
  camera: 'camera',
  lens: 'lens',
  focalLength: 'focal-length',
  aperture: 'aperture',
  shutterSpeed: 'shutter-speed',
  iso: 'iso',
};

interface ArtworkSpecsFormSectionProps {
  control: Control<any>;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
}

function SpecFieldRow({
  fieldKey,
  label,
  register,
  hiddenFields,
  setValue,
  showVisibilityToggle,
}: {
  fieldKey: SpecFieldKey;
  label: string;
  register: UseFormRegister<any>;
  hiddenFields: string[];
  setValue: UseFormSetValue<any>;
  showVisibilityToggle: boolean;
}) {
  const { t } = useTranslation('artwork');
  const isHidden = hiddenFields.includes(fieldKey);

  const toggleVisibility = (visible: boolean) => {
    const next = visible ? hiddenFields.filter((key) => key !== fieldKey) : [...hiddenFields, fieldKey];
    setValue('specs.hiddenFields', next, { shouldDirty: true });
  };

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label htmlFor={`specs-${fieldKey}`} className="text-sm font-medium">
          {label}
        </Label>
        {showVisibilityToggle && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">{t('artwork-form.specs.show-on-profile')}</span>
            <Switch id={`specs-visible-${fieldKey}`} checked={!isHidden} onCheckedChange={toggleVisibility} />
          </div>
        )}
      </div>
      <Input {...register(`specs.${fieldKey}`)} id={`specs-${fieldKey}`} className="mt-1" />
    </div>
  );
}

export function ArtworkSpecsFormSection({ control, register, setValue, watch }: ArtworkSpecsFormSectionProps) {
  const { t } = useTranslation('artwork');
  const { data: categoriesData, loading: categoriesLoading } = useQuery(GET_CATEGORIES);

  const mediumType = watch('specs.mediumType');
  const hiddenFields = watch('specs.hiddenFields') || [];

  const mediumCategoryGroups = useMemo(() => {
    const categories = categoriesData?.categories || [];
    const filtered = categories.filter(
      (cat: { facet?: { code?: string }; isActive?: boolean }) => cat.facet?.code === 'medium' && cat.isActive
    );

    const groups = new Map<string, { id: string; name: string }[]>();
    const ungrouped: { id: string; name: string }[] = [];

    for (const cat of filtered as { id: string; name: string; parent?: { name?: string } | null }[]) {
      const parentName = cat.parent?.name;
      if (parentName) {
        if (!groups.has(parentName)) groups.set(parentName, []);
        groups.get(parentName)!.push({ id: cat.id, name: cat.name });
      } else {
        ungrouped.push({ id: cat.id, name: cat.name });
      }
    }

    const sortedGroups = [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, items]) => ({
        label,
        items: items.sort((a, b) => a.name.localeCompare(b.name)),
      }));

    return { sortedGroups, ungrouped: ungrouped.sort((a, b) => a.name.localeCompare(b.name)) };
  }, [categoriesData]);

  const visibleFields =
    mediumType && mediumType in FIELDS_BY_MEDIUM ? FIELDS_BY_MEDIUM[mediumType as ArtworkMediumType] : [];

  return (
    <div className="mb-4 border-t border-gray-200 pt-4">
      <h3 className="text-base font-bold mb-1">{t('artwork-form.specs.section-title')}</h3>
      <p className="text-sm text-gray-500 mb-4">{t('artwork-form.specs.section-description')}</p>

      <div className="mb-4">
        <Label htmlFor="specs-mediumType" className="text-sm font-bold">
          {t('specs.medium-type')}
        </Label>
        <Controller
          name="specs.mediumType"
          control={control}
          render={({ field }) => (
            <Select value={field.value || ''} onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}>
              <SelectTrigger id="specs-mediumType" className="mt-2">
                <SelectValue placeholder={t('artwork-form.specs.medium-type-placeholder')} />
              </SelectTrigger>
              <SelectContent className="z-[200000]">
                <SelectItem value="none">{t('artwork-form.specs.medium-type-none')}</SelectItem>
                <SelectItem value="photography">{t('specs.medium-type-photography')}</SelectItem>
                <SelectItem value="traditional">{t('specs.medium-type-traditional')}</SelectItem>
                <SelectItem value="digital">{t('specs.medium-type-digital')}</SelectItem>
                <SelectItem value="mixed">{t('specs.medium-type-mixed')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {mediumType && (
        <div className="mb-4">
          <Label htmlFor="specs-mediumCategoryId" className="text-sm font-bold">
            {t('artwork-form.specs.medium-category-label')}
          </Label>
          <p className="text-xs text-gray-500 mb-2">{t('artwork-form.specs.medium-category-hint')}</p>
          <Controller
            name="specs.mediumCategoryId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || ''}
                onValueChange={(value) => field.onChange(value === 'none' ? '' : value)}
                disabled={categoriesLoading}
              >
                <SelectTrigger id="specs-mediumCategoryId" className="mt-1">
                  <SelectValue
                    placeholder={
                      categoriesLoading
                        ? t('artwork-form.specs.medium-category-loading')
                        : t('artwork-form.specs.medium-category-placeholder')
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[200000] max-h-60">
                  <SelectItem value="none">{t('artwork-form.specs.medium-category-none')}</SelectItem>
                  {mediumCategoryGroups.ungrouped.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                  {mediumCategoryGroups.sortedGroups.map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel>{group.label}</SelectLabel>
                      {group.items.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {visibleFields.map((fieldKey) => (
        <SpecFieldRow
          key={fieldKey}
          fieldKey={fieldKey}
          label={t(`specs.${FIELD_LABEL_KEYS[fieldKey]}`)}
          register={register}
          hiddenFields={hiddenFields}
          setValue={setValue}
          showVisibilityToggle
        />
      ))}
    </div>
  );
}

function hiddenFieldsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

export function buildSpecsMetaInput(specs: ArtworkSpecsFormValues, initial?: ArtworkSpecsFormValues) {
  const currentHidden = specs.hiddenFields ?? [];
  const initialHidden = initial?.hiddenFields ?? [];
  const hidden = hiddenFieldsEqual(currentHidden, initialHidden) ? undefined : currentHidden;

  const input: Record<string, unknown> = {
    mediumType: specs.mediumType ? specs.mediumType.toUpperCase() : undefined,
    medium: specs.medium?.trim() || null,
    technique: specs.technique?.trim() || null,
    subMedium: specs.subMedium?.trim() || null,
    software: specs.software?.trim() || null,
    camera: specs.camera?.trim() || null,
    lens: specs.lens?.trim() || null,
    focalLength: specs.focalLength?.trim() || null,
    aperture: specs.aperture?.trim() || null,
    shutterSpeed: specs.shutterSpeed?.trim() || null,
    iso: specs.iso?.trim() || null,
    hidden,
  };

  const initialCategoryId = initial?.mediumCategoryId ?? '';
  if (!initial || specs.mediumCategoryId !== initialCategoryId) {
    input.mediumCategoryId = specs.mediumCategoryId || null;
  }

  return input;
}

export function specsFormChanged(current: ArtworkSpecsFormValues, initial: ArtworkSpecsFormValues): boolean {
  return JSON.stringify(current) !== JSON.stringify(initial);
}

export function artworkSpecsToFormValues(
  artworkSpecs?: {
    mediumType?: string | null;
    medium?: string | null;
    technique?: string | null;
    subMedium?: string | null;
    software?: string | null;
    camera?: string | null;
    lens?: string | null;
    focalLength?: string | null;
    aperture?: string | null;
    shutterSpeed?: string | null;
    iso?: string | null;
    mediumCategory?: { id: string } | null;
    hidden?: string[] | null;
  } | null
): ArtworkSpecsFormValues {
  const mediumTypeValues: ArtworkMediumType[] = ['photography', 'traditional', 'digital', 'mixed'];
  const normalizedMediumType = artworkSpecs?.mediumType?.toLowerCase();
  const mediumType =
    normalizedMediumType && mediumTypeValues.includes(normalizedMediumType as ArtworkMediumType)
      ? (normalizedMediumType as ArtworkMediumType)
      : '';

  return {
    mediumType,
    medium: artworkSpecs?.medium || '',
    technique: artworkSpecs?.technique || '',
    subMedium: artworkSpecs?.subMedium || '',
    software: artworkSpecs?.software || '',
    camera: artworkSpecs?.camera || '',
    lens: artworkSpecs?.lens || '',
    focalLength: artworkSpecs?.focalLength || '',
    aperture: artworkSpecs?.aperture || '',
    shutterSpeed: artworkSpecs?.shutterSpeed || '',
    iso: artworkSpecs?.iso || '',
    mediumCategoryId: artworkSpecs?.mediumCategory?.id || '',
    hiddenFields: artworkSpecs?.hidden ? [...artworkSpecs.hidden] : [],
  };
}

export const EMPTY_SPECS_FORM_VALUES: ArtworkSpecsFormValues = {
  mediumType: '',
  medium: '',
  technique: '',
  subMedium: '',
  software: '',
  camera: '',
  lens: '',
  focalLength: '',
  aperture: '',
  shutterSpeed: '',
  iso: '',
  mediumCategoryId: '',
  hiddenFields: [],
};
